import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './VerifyEmail.css';

function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { verifyEmail } = useAuth();
    const [status, setStatus] = useState('loading');
    const [message, setMessage] = useState('Verifying your email...');

    useEffect(() => {
        const token = searchParams.get('token');
        if (!token) {
            setStatus('error');
            setMessage('This verification link is missing a token.');
            return;
        }

        let cancelled = false;
        let redirectTimeout;
        async function verify() {
            try {
                await verifyEmail(token);
                if (!cancelled) {
                    setStatus('success');
                    setMessage('Your email has been verified. Redirecting you to Hockey Pipeline...');
                    redirectTimeout = setTimeout(() => {
                        navigate('/', { replace: true });
                    }, 900);
                }
            } catch (err) {
                if (!cancelled) {
                    const detail = err.response?.data?.detail;
                    setStatus('error');
                    setMessage(detail || 'This verification link is invalid or expired.');
                }
            }
        }

        verify();
        return () => {
            cancelled = true;
            if (redirectTimeout) {
                clearTimeout(redirectTimeout);
            }
        };
    }, [navigate, searchParams, verifyEmail]);

    return (
        <Box className="verify-email-page">
            <Helmet>
                <title>Verify Email | Hockey Pipeline</title>
            </Helmet>

            <Card className="verify-email-card" elevation={0}>
                <CardContent sx={{ p: 3 }}>
                    <div className="verify-email-logo">
                        <img src="/logo@4x.png" alt="Hockey Pipeline" />
                    </div>

                    <Typography variant="h4" component="h1" className="verify-email-title">
                        Email Verification
                    </Typography>

                    {status === 'loading' ? (
                        <Box className="verify-email-loading">
                            <CircularProgress size={28} sx={{ color: 'var(--brand-strong)' }} />
                            <Typography variant="body1">{message}</Typography>
                        </Box>
                    ) : (
                        <Alert
                            severity={status === 'success' ? 'success' : 'error'}
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                backgroundColor: status === 'success' ? '#102417' : '#2d1515',
                                color: status === 'success' ? '#bbf7d0' : '#fca5a5',
                                '& .MuiAlert-icon': {
                                    color: status === 'success' ? '#86efac' : '#f87171',
                                },
                            }}
                        >
                            {message}
                        </Alert>
                    )}

                    {status === 'error' && (
                        <Button
                            onClick={() => navigate('/', { replace: true })}
                            fullWidth
                            variant="contained"
                            className="verify-email-button"
                            disableElevation
                        >
                            Go to Hockey Pipeline
                        </Button>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default VerifyEmail;
